import React, { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem('cbm_cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [isOpen, setIsOpen] = useState(false);

  const addItem = useCallback((product, quantity = 1, size = null) => {
    setItems(prev => {
      const existingIdx = prev.findIndex(
        i => i.product_id === product.id && i.size === size
      );
      if (existingIdx >= 0) {
        const updated = [...prev];
        updated[existingIdx] = {
          ...updated[existingIdx],
          quantity: updated[existingIdx].quantity + quantity
        };
        return updated;
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        image_url: product.image_url,
        quantity,
        size
      }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((product_id, size) => {
    setItems(prev => prev.filter(
      i => !(i.product_id === product_id && i.size === size)
    ));
  }, []);

  const updateQuantity = useCallback((product_id, size, quantity) => {
    if (quantity <= 0) {
      removeItem(product_id, size);
      return;
    }
    setItems(prev => prev.map(i =>
      i.product_id === product_id && i.size === size
        ? { ...i, quantity }
        : i
    ));
  }, [removeItem]);

  const clearCart = useCallback(() => setItems([]), []);

  React.useEffect(() => {
    localStorage.setItem('cbm_cart', JSON.stringify(items));
  }, [items]);

  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items, isOpen, setIsOpen,
      addItem, removeItem, updateQuantity, clearCart,
      total, itemCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
